/** The design system's tab bar, as real buttons.
 *
 *  `components/chrome/TabBar.jsx` renders its tabs as `<span onClick>`, so they
 *  are not focusable and have no accessible role. Wrapping each one in a button
 *  fixed that but made every tab draw its own container rule, because the
 *  component paints a row, not a cell.
 *
 *  So this reproduces the component's visual spec — four words, the active one
 *  in amber at full weight, never an icon, 48px targets, padded for the home
 *  indicator — using semantic controls. It is a deliberate re-implementation of
 *  one component, not a redesign: the tokens and measurements are the design
 *  system's. Reported upstream; when TabBar grows real semantics, delete this.
 */
export function TabBarNav({
  tabs,
  active,
  onSelect,
}: {
  tabs: string[];
  active: string;
  onSelect: (tab: string) => void;
}) {
  return (
    <nav
      aria-label="Sections"
      style={{
        display: 'flex',
        padding: '0 var(--bujo-gutter)',
        paddingBottom: 'calc(22px + env(safe-area-inset-bottom))',
      }}
    >
      {tabs.map((tab) => {
        const current = tab === active;
        return (
          <button
            key={tab}
            type="button"
            aria-current={current ? 'page' : undefined}
            onClick={() => onSelect(tab)}
            style={{
              flex: 1,
              minHeight: 'var(--bujo-tap-min)',
              padding: '14px 0',
              textAlign: 'left',
              cursor: 'pointer',
              font: `${current ? 'var(--bujo-fw-bold)' : 'var(--bujo-fw-regular)'} var(--bujo-fs-label)/var(--bujo-lh-tight) var(--bujo-font-mono)`,
              letterSpacing: 'var(--bujo-ls-label)',
              color: current ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)',
            }}
          >
            {tab}
          </button>
        );
      })}
    </nav>
  );
}
